<template>
  <!-- 관리 설정 초안 확인 HITL 카드(AA-119, D10 공용 계약) — 사용자가 [등록]을 눌러야 실제 커밋된다.
       DraftConfirmBlock 골격 복제. GROUP_COMMIT_KEYS 같은 하드코딩 없이 candidates[].commit_key /
       input_fields[].key 를 서버가 내려주는 그대로 v-for 렌더 + 동적 키로 선택/입력값을 수집한다
       (AA-120 부품 체인의 part_1_name 류 동적 인덱스 필드도 동일 경로로 그대로 통과). -->
  <div class="aw-draft">
    <div class="aw-draft-title">{{ data.title }}</div>
    <dl class="aw-draft-list">
      <template v-for="(row, i) in previewRows" :key="'p' + i">
        <dt>{{ row.label }}</dt>
        <dd>{{ row.value }}</dd>
      </template>
    </dl>
    <div v-if="notice" class="aw-draft-warn">{{ notice }}</div>
    <div v-if="missingList.length" class="aw-draft-warn">
      {{ $t('assistant.draftMissing', { fields: missingList.join(', ') }) }}
    </div>
    <!-- 후보 그룹(candidates) — field 는 표시 라벨, commit_key 는 서버 주도 commit 페이로드 키.
         multi:true 그룹은 다건 토글, 아니면 단일 선택(재클릭 = 해제). -->
    <div v-if="!done && candidateGroups.length" class="aw-draft-candidates">
      <div class="aw-draft-cand-hint">{{ $t('assistant.adminCandidateHint') }}</div>
      <div v-for="g in candidateGroups" :key="g.commit_key" class="aw-draft-cand-group">
        <span class="aw-draft-cand-field">{{ g.field }}</span>
        <button
          v-for="(c, i) in g.items"
          :key="g.commit_key + i"
          type="button"
          class="aw-draft-cand-chip"
          :class="{ 'aw-draft-cand-chip--selected': isSelected(g, i) }"
          :disabled="committing"
          @click="onChipClick(g, i)"
        >
          {{ c.label }}
        </button>
      </div>
    </div>
    <!-- 직접 입력 필드(input_fields) — key 그대로 inputs 에 수집(서버가 임의 개수 내려줌). -->
    <div v-if="!done && inputFieldList.length" class="aw-draft-optional">
      <div v-for="f in inputFieldList" :key="f.key" class="aw-draft-cand-group">
        <span class="aw-draft-cand-field">{{ f.label }}</span>
        <input
          class="aw-draft-input"
          type="text"
          :aria-label="f.label"
          :placeholder="f.placeholder || f.label"
          :disabled="committing"
          :value="inputs[f.key] || ''"
          @input="onInput(f.key, $event.target.value)"
        >
      </div>
    </div>
    <div v-if="committing && !done" class="aw-draft-status">{{ $t('assistant.committing') }}</div>
    <div v-if="!done" class="aw-draft-actions">
      <button
        type="button"
        class="aw-draft-btn aw-draft-commit"
        :disabled="registerDisabled"
        @click="$emit('commit', commitPayload)"
      >
        {{ $t('assistant.register') }}
      </button>
      <button
        type="button"
        class="aw-draft-btn aw-draft-cancel"
        :disabled="committing"
        @click="$emit('cancel')"
      >
        {{ $t('assistant.cancel') }}
      </button>
    </div>
  </div>
</template>

<script>
/**
 * @component AdminActionDraftBlock
 * @description HITL 관리 설정 초안 확인 카드(AA-119, D10 generic 카드). AA-120/121 도 재사용한다.
 *   data={draft_id, confirmation_token, action, title, preview:[{label,value}], candidates?:
 *   [{field, commit_key, items:[{code,label}], multi?}], input_fields?:[{key,label,required?,
 *   placeholder?}], missing?, notice?}. 컴포넌트는 필드 이름을 하드코딩하지 않는다 — candidates/
 *   input_fields 는 서버가 내려주는 임의 길이 목록이며 commit_key/key 로만 값을 수집·조립한다.
 *   - candidates: 코드 그룹 로컬 선택(단일=재클릭 해제, multi=다건 토글). LLM 재호출 없음.
 *   - input_fields: 텍스트 직접 입력, required 필드만 [등록] 게이트 대상(비-required 는 값 있을 때만 전송).
 *   - [등록]: candidates 전 그룹 선택 완료(multi 는 1개 이상) + required input_fields 전부 기입 시 활성.
 *     클릭 시 {selections:{commit_key: code|[codes]}, inputs:{key: value}} 를 commit 페이로드로 emit —
 *     BE _commit_admin() 이 draft.allowed_selections 화이트리스트로 재검증한다(§4.5).
 */
export default {
  name: 'AdminActionDraftBlock',
  props: {
    data: { type: Object, default: () => ({}) },
    committing: { type: Boolean, default: false },
    done: { type: Boolean, default: false }
  },
  emits: ['commit', 'cancel', 'refine'],
  data() {
    return {
      selected: {}, // {commit_key: 칩 index(단일) | 칩 index[](multi)}
      inputs: {} // {input_fields key: 입력 텍스트}
    }
  },
  computed: {
    previewRows() {
      return Array.isArray(this.data.preview) ? this.data.preview : []
    },
    notice() {
      return this.data.notice || ''
    },
    missingList() {
      return Array.isArray(this.data.missing) ? this.data.missing : []
    },
    candidateGroups() {
      return Array.isArray(this.data.candidates) ? this.data.candidates : []
    },
    inputFieldList() {
      const list = Array.isArray(this.data.input_fields) ? this.data.input_fields : []
      return list.filter(f => f && f.key && f.label)
    },
    /** 값이 입력된 필드 — {key: trim 값} */
    filledInputs() {
      const out = {}
      for (const f of this.inputFieldList) {
        const v = String(this.inputs[f.key] || '').trim()
        if (v) out[f.key] = v
      }
      return out
    },
    /** commit 페이로드 — 선택된 후보 그룹 코드 + 입력값 (BE 화이트리스트/필수값 검증 전제) */
    commitPayload() {
      const selections = {}
      for (const g of this.candidateGroups) {
        if (g.multi) {
          const codes = (this.selected[g.commit_key] || [])
            .map(i => this.chipCode((g.items || [])[i])).filter(Boolean)
          if (codes.length) selections[g.commit_key] = codes
          continue
        }
        const i = this.selected[g.commit_key]
        const code = i !== undefined ? this.chipCode((g.items || [])[i]) : ''
        if (code) selections[g.commit_key] = code
      }
      return { selections, inputs: { ...this.filledInputs } }
    },
    /** [등록] 비활성 — candidates 그룹 미선택 또는 required input_fields 미기입 존재 시 */
    registerDisabled() {
      if (this.committing) return true
      for (const g of this.candidateGroups) {
        if (g.multi) {
          if (!(this.selected[g.commit_key] || []).length) return true
        } else if (this.selected[g.commit_key] === undefined || !(g.items || [])[this.selected[g.commit_key]]) {
          return true
        }
      }
      for (const f of this.inputFieldList) {
        if (f.required && !this.filledInputs[f.key]) return true
      }
      return false
    }
  },
  methods: {
    chipCode(c) {
      return (c && c.code !== undefined && c.code !== null) ? String(c.code) : ''
    },
    isSelected(g, i) {
      const sel = this.selected[g.commit_key]
      if (g.multi) return Array.isArray(sel) && sel.includes(i)
      return sel === i
    },
    onChipClick(g, i) {
      if (g.multi) {
        const sel = this.selected[g.commit_key] || []
        const idx = sel.indexOf(i)
        if (idx >= 0) sel.splice(idx, 1) // 재클릭 = 해당 항목만 해제
        else sel.push(i)
        if (sel.length) this.selected[g.commit_key] = [...sel]
        else delete this.selected[g.commit_key]
        return
      }
      const prev = this.selected[g.commit_key]
      if (prev === i) delete this.selected[g.commit_key] // 재클릭 = 해제
      else this.selected[g.commit_key] = i
    },
    onInput(key, val) {
      this.inputs[key] = val
    }
  }
}
</script>

<style scoped>
.aw-draft {
  max-width: 100%;
  background: var(--color-primary-50, #E8EDF4);
  border: 1px solid var(--color-primary-100, #C5D1E3);
  border-radius: 12px;
  padding: 12px;
  white-space: normal;
}
.aw-draft-title { font-weight: 700; font-size: 14px; color: var(--color-primary-500, #3B5998); margin-bottom: 8px; }
.aw-draft-list { display: grid; grid-template-columns: auto 1fr; gap: 4px 10px; margin: 0 0 8px; font-size: 13px; }
.aw-draft-list dt { font-weight: 700; color: var(--color-gray-800, #202124); white-space: nowrap; }
.aw-draft-list dd { margin: 0; color: var(--color-gray-800, #202124); word-break: break-word; }
.aw-draft-warn {
  background: var(--color-warning-light, #F5EDDA);
  color: var(--color-warning, #C8870A);
  border: 1px solid var(--color-warning, #C8870A);
  border-radius: 8px; padding: 6px 8px; font-size: 12.5px; margin-bottom: 8px;
}
.aw-draft-status { font-size: 12.5px; color: var(--color-gray-600, #5f6368); margin-bottom: 6px; }
.aw-draft-candidates { margin-bottom: 8px; }
.aw-draft-optional { margin-bottom: 8px; }
.aw-draft-cand-hint { font-size: 12px; color: var(--color-gray-600, #5f6368); margin-bottom: 6px; }
.aw-draft-cand-group { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-bottom: 6px; }
.aw-draft-cand-field { font-size: 12px; font-weight: 700; color: var(--color-gray-800, #202124); white-space: nowrap; }
.aw-draft-cand-chip {
  border: 1px solid var(--color-primary-500, #3B5998);
  background: var(--color-white, #fff);
  color: var(--color-primary-600, #2C4578);
  border-radius: var(--radius-full, 9999px);
  padding: 4px 12px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.aw-draft-cand-chip:hover:not(:disabled) { background: var(--color-primary-50, #E8EDF4); }
.aw-draft-cand-chip:disabled { border-color: var(--color-gray-300, #b0bec5); color: var(--color-gray-400, #9aa0a6); cursor: not-allowed; }
.aw-draft-cand-chip--selected { background: var(--color-primary-500, #3B5998); color: var(--color-white, #fff); }
.aw-draft-cand-chip--selected:hover:not(:disabled) { background: var(--color-primary-600, #2C4578); }
.aw-draft-input {
  flex: 1 1 160px; min-width: 0; border: 1px solid var(--color-gray-300, #b0bec5);
  background: var(--color-white, #fff); color: var(--color-gray-800, #202124);
  border-radius: var(--radius-md, 8px); padding: 5px 8px; font-size: 12.5px;
}
.aw-draft-input:focus { outline: none; border-color: var(--color-primary-500, #3B5998); }
.aw-draft-input:disabled {
  background: var(--color-gray-100, #eef0f3); border-color: var(--color-gray-300, #b0bec5); color: var(--color-gray-400, #9aa0a6);
}
.aw-draft-actions { display: flex; gap: 8px; }
.aw-draft-btn { border: none; border-radius: var(--radius-md, 8px); padding: 7px 16px; font-size: 13px; font-weight: 600; cursor: pointer; }
.aw-draft-commit { background: var(--color-primary-500, #3B5998); color: var(--color-white, #fff); }
.aw-draft-commit:hover:not(:disabled) { background: var(--color-primary-600, #2C4578); }
.aw-draft-cancel { background: var(--color-gray-100, #eef0f3); color: var(--color-gray-800, #202124); }
.aw-draft-cancel:hover:not(:disabled) { background: var(--color-gray-200, #e0e0e0); }
.aw-draft-btn:disabled { background: var(--color-gray-300, #b0bec5); color: var(--color-white, #fff); cursor: not-allowed; }
</style>
